import React, { useMemo, useState } from "react";
import { attributionLabel } from "./DetailPanel";

// The accessible equivalent of the diagram, the mobile primary view, and the
// fastest way to find a specific path. It shares the ONE selection value, so
// the diagram and the table are two views of the same state.

function ColorCell({ name, hex, quadrants }) {
  const quartered = quadrants && quadrants.length === 4;
  return (
    <span className="cell__color">
      {quartered ? (
        <span className="swatch swatch--quartered" aria-hidden="true">
          {quadrants.map((color, i) => (
            <span key={i} style={{ background: color }} />
          ))}
        </span>
      ) : (
        <span className="swatch" style={{ background: hex }} aria-hidden="true" />
      )}
      {/* The wording is the doctrine; the hex is a screen approximation. */}
      <span>{name}</span>
    </span>
  );
}

function SortableHeader({ label, column, sort, onSort, numeric }) {
  const active = sort.column === column;
  const direction = active ? sort.direction : "none";
  return (
    <th
      scope="col"
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
      className={numeric ? "table__th table__th--numeric" : "table__th"}
    >
      <button type="button" className="table__sort" onClick={() => onSort(column)}>
        {label}
        <span aria-hidden="true" className="table__caret">
          {active ? (direction === "asc" ? "↑" : "↓") : ""}
        </span>
      </button>
    </th>
  );
}

function useSort(initial) {
  const [sort, setSort] = useState({ column: initial, direction: "asc" });
  const onSort = (column) =>
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" }
    );

  const apply = (rows) =>
    [...rows].sort((a, b) => {
      const av = a[sort.column];
      const bv = b[sort.column];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      const order = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sort.direction === "asc" ? order : -order;
    });

  return [sort, onSort, apply];
}

// Declared rather than hardcoded: a column that no row in this tradition has a
// value for is dropped. Jewish mode gets Partzuf, World, Soul level and
// Shepherd and no colour scales; Hermetic gets the scales and no partzufim.
// Neither has a bespoke component.
const SEPHIRAH_COLUMNS = [
  { key: "number", label: "#", numeric: true, sortable: true },
  { key: "name", label: "Name", rowHeader: true, sortable: true },
  { key: "title", label: "Title", sortable: true },
  { key: "hebrew", label: "Hebrew", hebrew: true },
  { key: "pillar", label: "Pillar", sortable: true },
  { key: "planet", label: "Planet", sortable: true },
  { key: "archangel", label: "Archangel", linkKey: "archangelPath", sortable: true },
  { key: "partzuf", label: "Partzuf", sortable: true },
  { key: "world", label: "World", sortable: true },
  { key: "soulLevel", label: "Soul level", sortable: true },
  { key: "shepherd", label: "Shepherd", sortable: true },
  // All four of 777's columns, in the order the book prints them. A tradition
  // that carries none of them drops all four, which is what `visibleColumns`
  // is for — the Jewish trees must not grow a Golden Dawn column headed with
  // nulls. Only the Queen cell is ever quartered: Malkuth's four elemental
  // quarters are printed in that column and in no other.
  { key: "colorKing", label: "King scale", colorHexKey: "colorKingHex" },
  { key: "colorQueen", label: "Queen scale", colorHexKey: "colorQueenHex", quadrants: true },
  { key: "colorPrince", label: "Prince scale", colorHexKey: "colorPrinceHex" },
  { key: "colorPrincess", label: "Princess scale", colorHexKey: "colorPrincessHex" }
];

// A row is a select target AND it carries cross-reference links — the archangel
// and the Tarot trump. Both are wanted, but the row handler was swallowing the
// link: clicking the middle of Hod's row navigated to /archangels/michael
// instead of selecting Hod. A click that started on a link is the link's.
function selectRow(onSelect, node) {
  return (event) => {
    if (event.target.closest("a")) return;
    onSelect(node);
  };
}

function present(value) {
  return value !== null && value !== undefined && value !== "";
}

function visibleColumns(columns, rows) {
  return columns.filter((column) => {
    // Colour is doctrine in some traditions and UI chrome in others. Where it
    // is chrome, it is not a column.
    if (column.colorHexKey && rows.every((r) => r.colorIsTraditional === false)) return false;
    return rows.some((r) => present(r[column.key]));
  });
}

// A lens answers the same question in either view, so it stays live here: a
// region lens becomes row grouping, a sequence lens becomes the sort order with
// its step commentary in the column that usually holds the summary.
function groupRows(rows, regionOverlay, key = "slug") {
  if (!regionOverlay) return [{ group: null, rows }];

  const seen = new Set();
  const grouped = regionOverlay.groups.map((group) => {
    const slugs = new Set(
      group.items.filter((i) => i.kind === "sephirah").map((i) => i.slug)
    );
    const members = rows.filter((r) => slugs.has(r[key]));
    members.forEach((r) => seen.add(r[key]));
    return { group, rows: members };
  });

  const rest = rows.filter((r) => !seen.has(r[key]));
  if (rest.length) grouped.push({ group: { slug: "ungrouped", name: "Not in this lens" }, rows: rest });
  return grouped.filter((g) => g.rows.length > 0);
}

function sequenceOrder(rows, sequence, key = "slug") {
  if (!sequence) return { rows, notes: null };
  const order = new Map();
  const notes = new Map();
  sequence.steps.forEach((step, i) => {
    order.set(step.slug, i);
    notes.set(step.slug, { index: i + 1, note: step.note });
  });
  const ordered = [...rows].sort(
    (a, b) => (order.has(a[key]) ? order.get(a[key]) : 1e9) -
              (order.has(b[key]) ? order.get(b[key]) : 1e9)
  );
  return { rows: ordered, notes };
}

function SephirothTable({ sephiroth, selected, onSelect, regionOverlay, sequence }) {
  const [sort, onSort, apply] = useSort("number");
  // ONE call. It was invoked twice — once for the rows and once for the notes —
  // which sorted the whole table twice to produce two halves of one answer.
  const { rows, notes } = sequence
    ? sequenceOrder(sephiroth, sequence)
    : { rows: apply(sephiroth), notes: null };
  const groups = groupRows(rows, regionOverlay);
  const columns = visibleColumns(SEPHIRAH_COLUMNS, sephiroth);
  const counted = sephiroth.filter((s) => s.countedInTen !== false).length;
  const uncounted = sephiroth.length - counted;
  const span = columns.length + (notes ? 1 : 0);

  return (
    <table className="table">
      <caption className="table__caption">
        {sephiroth.length} nodes
        {uncounted > 0 ? `, ${counted} of them counted among the ten` : ""}.
        {sequence ? ` In ${sequence.name} order.` : ""}
        {regionOverlay ? ` Grouped by ${regionOverlay.name}.` : ""} Select a row to
        open its correspondences.
      </caption>
      <thead>
        <tr>
          {notes && (
            <th scope="col" className="table__th table__th--numeric">
              Step
            </th>
          )}
          {columns.map((column) =>
            column.sortable ? (
              <SortableHeader
                key={column.key}
                label={column.label}
                column={column.key}
                sort={sort}
                onSort={onSort}
                numeric={column.numeric}
              />
            ) : (
              <th key={column.key} scope="col" className="table__th">
                {column.label}
              </th>
            )
          )}
        </tr>
        {notes && (
          <tr>
            <th scope="col" className="table__th" colSpan={span}>
              Commentary
            </th>
          </tr>
        )}
      </thead>
      {groups.map(({ group, rows: groupRowsList }) => (
      <tbody key={group ? group.slug : "all"}>
        {group && (
          <tr className="table__subhead" data-testid={`group-${group.slug}`}>
            <th scope="colgroup" colSpan={span}>
              <span
                className="legend__swatch"
                style={{ background: group.colorHex || "#6d7684" }}
                aria-hidden="true"
              />
              {group.name}
            </th>
          </tr>
        )}
        {groupRowsList.map((s) => (
          <tr
            key={s.slug}
            className={
              selected && selected.type === "sephirah" && selected.slug === s.slug
                ? "table__row table__row--selected"
                : "table__row"
            }
            aria-selected={
              Boolean(selected) && selected.type === "sephirah" && selected.slug === s.slug
            }
            data-testid={`row-sephirah-${s.slug}`}
            onClick={selectRow(onSelect, { type: "sephirah", slug: s.slug })}
          >
            {notes && (
              <td className="table__td table__td--numeric">
                {notes.get(s.slug) ? notes.get(s.slug).index : ""}
              </td>
            )}
            {columns.map((column) => {
              if (column.rowHeader) {
                return (
                  <th key={column.key} scope="row" className="table__rowhead">
                    <button type="button" className="table__select">{s[column.key]}</button>
                  </th>
                );
              }
              if (column.colorHexKey) {
                return (
                  <td key={column.key} className="table__td">
                    <ColorCell
                      name={s[column.key]}
                      hex={s[column.colorHexKey]}
                      quadrants={column.quadrants ? s.colorQueenQuadrants : null}
                    />
                  </td>
                );
              }
              if (column.linkKey && s[column.linkKey]) {
                return (
                  <td key={column.key} className="table__td">
                    <a className="archangel-link" href={s[column.linkKey]}>
                      {s[column.key]}
                    </a>
                  </td>
                );
              }
              return (
                <td
                  key={column.key}
                  className={column.numeric ? "table__td table__td--numeric" : "table__td"}
                  dir={column.hebrew ? "rtl" : undefined}
                  lang={column.hebrew ? "he" : undefined}
                >
                  {s[column.key]}
                </td>
              );
            })}
          </tr>
        ))}
        {notes &&
          groupRowsList.map((s) =>
            notes.get(s.slug) && notes.get(s.slug).note ? (
              <tr key={`note-${s.slug}`} className="table__noterow">
                <td className="table__td table__note" colSpan={span}>
                  {notes.get(s.slug).note}
                </td>
              </tr>
            ) : null
          )}
      </tbody>
      ))}
    </table>
  );
}

const ANY = "any";

function Filter({ label, value, options, onChange }) {
  if (options.length < 2) return null;
  return (
    <label className="filter">
      <span className="filter__label">{label}</span>
      <select
        className="filter__select"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value={ANY}>All</option>
        {options.map((option) => (
          <option key={String(option)} value={String(option)}>
            {String(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function unique(values) {
  return [...new Set(values.filter((v) => v !== null && v !== undefined && v !== ""))].sort();
}

function PathsTable({ paths, selected, onSelect, sequence }) {
  const [sort, onSort, apply] = useSort("number");
  const [letterClass, setLetterClass] = useState(ANY);
  const [attributionType, setAttributionType] = useState(ANY);
  const [pillar, setPillar] = useState(ANY);
  const [abyss, setAbyss] = useState(ANY);

  const filtered = useMemo(
    () =>
      paths.filter((p) => {
        if (letterClass !== ANY && p.letterClass !== letterClass) return false;
        if (attributionType !== ANY && p.attributionType !== attributionType) return false;
        if (pillar !== ANY && (p.pillar || "none") !== pillar) return false;
        if (abyss !== ANY && String(p.crossesAbyss) !== abyss) return false;
        return true;
      }),
    [paths, letterClass, attributionType, pillar, abyss]
  );

  const { rows, notes } = sequence
    ? sequenceOrder(filtered, sequence)
    : { rows: apply(filtered), notes: null };

  return (
    <>
      <div className="filters" role="group" aria-label="Filter paths">
        <Filter
          label="Letter class"
          value={letterClass}
          options={unique(paths.map((p) => p.letterClass))}
          onChange={setLetterClass}
        />
        <Filter
          label="Attribution"
          value={attributionType}
          options={unique(paths.map((p) => p.attributionType))}
          onChange={setAttributionType}
        />
        <Filter
          label="Pillar"
          value={pillar}
          options={unique(paths.map((p) => p.pillar || "none"))}
          onChange={setPillar}
        />
        <Filter
          label="Crosses the Abyss"
          value={abyss}
          options={unique(paths.map((p) => String(p.crossesAbyss)))}
          onChange={setAbyss}
        />
      </div>

      <table className="table">
        <caption className="table__caption">
          {rows.length} of {paths.length} paths.
          {sequence ? ` In ${sequence.name} order.` : ""} Select a row to open its
          correspondences.
        </caption>
        <thead>
          <tr>
            {notes && (
              <th scope="col" className="table__th table__th--numeric">
                Step
              </th>
            )}
            <SortableHeader label="#" column="number" sort={sort} onSort={onSort} numeric />
            <th scope="col" className="table__th">Letter</th>
            <SortableHeader label="Name" column="letterName" sort={sort} onSort={onSort} />
            <SortableHeader label="Meaning" column="letterMeaning" sort={sort} onSort={onSort} />
            <SortableHeader label="Value" column="letterValue" sort={sort} onSort={onSort} numeric />
            <SortableHeader label="Class" column="letterClass" sort={sort} onSort={onSort} />
            <SortableHeader label="Trump" column="tarotNumber" sort={sort} onSort={onSort} />
            <SortableHeader label="Attribution" column="attribution" sort={sort} onSort={onSort} />
            <th scope="col" className="table__th">King scale</th>
            <th scope="col" className="table__th">Queen scale</th>
            <th scope="col" className="table__th">Prince scale</th>
            <th scope="col" className="table__th">Princess scale</th>
            <SortableHeader label="Joins" column="fromName" sort={sort} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr
              key={p.slug}
              className={
                selected && selected.type === "path" && selected.slug === p.slug
                  ? "table__row table__row--selected"
                  : "table__row"
              }
              aria-selected={
                Boolean(selected) && selected.type === "path" && selected.slug === p.slug
              }
              data-testid={`row-path-${p.slug}`}
              onClick={selectRow(onSelect, { type: "path", slug: p.slug })}
            >
              {notes && (
                <td className="table__td table__td--numeric">
                  {notes.get(p.slug) ? notes.get(p.slug).index : ""}
                </td>
              )}
              <td className="table__td table__td--numeric">{p.number}</td>
              <td className="table__td" dir="rtl" lang="he">{p.hebrewLetter}</td>
              <th scope="row" className="table__rowhead">
                <button type="button" className="table__select">{p.letterName}</button>
              </th>
              <td className="table__td">{p.letterMeaning}</td>
              <td className="table__td table__td--numeric">{p.letterValue}</td>
              <td className="table__td">{p.letterClass}</td>
              <td className="table__td">
                {p.tarotPath ? (
                  <a className="tarot-card-link" href={p.tarotPath}>
                    {p.tarot} ({p.tarotNumber})
                  </a>
                ) : p.tarot ? `${p.tarot} (${p.tarotNumber})` : ""}
              </td>
              <td className="table__td">{attributionLabel(p)}</td>
              <td className="table__td">
                <ColorCell name={p.colorKing} hex={p.colorKingHex} />
              </td>
              <td className="table__td">
                <ColorCell name={p.colorQueen} hex={p.colorQueenHex} />
              </td>
              <td className="table__td">
                <ColorCell name={p.colorPrince} hex={p.colorPrinceHex} />
              </td>
              <td className="table__td">
                <ColorCell name={p.colorPrincess} hex={p.colorPrincessHex} />
              </td>
              <td className="table__td">
                {p.fromName} &rarr; {p.toName}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export default function NodeTables({
  sephiroth,
  paths,
  selected,
  onSelect,
  pathsNote,
  regionOverlay = null,
  sequence = null
}) {
  const [tab, setTab] = useState("sephiroth");
  // A tradition with no settled path set gets no paths tab. It gets the reason.
  const hasPaths = paths.length > 0;
  const active = hasPaths ? tab : "sephiroth";

  const tabs = [["sephiroth", `Sephiroth (${sephiroth.length})`]];
  if (hasPaths) tabs.push(["paths", `Paths (${paths.length})`]);

  return (
    <section className="tables" aria-label="Tree of Life data">
      {/* Unlike the Tree/Table switch above it, these really are two panels of
          content, so the tab roles are right — and that means the keyboard
          contract that goes with them has to actually work: one tab stop for
          the set, arrows to move within it. Announcing "tab, 1 of 2" and then
          ignoring the arrow keys is worse than not announcing it at all. */}
      <div className="tabs" role="tablist" aria-label="Which nodes to list">
        {tabs.map(([id, label], index) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`tab-${id}`}
            aria-selected={active === id}
            aria-controls={`panel-${id}`}
            tabIndex={active === id ? 0 : -1}
            className={active === id ? "tabs__tab tabs__tab--active" : "tabs__tab"}
            onClick={() => setTab(id)}
            onKeyDown={(event) => {
              const last = tabs.length - 1;
              const to = {
                ArrowRight: index === last ? 0 : index + 1,
                ArrowLeft: index === 0 ? last : index - 1,
                Home: 0,
                End: last
              }[event.key];
              if (to === undefined) return;
              event.preventDefault();
              const [id] = tabs[to];
              setTab(id);
              event.currentTarget.parentNode.querySelector(`#tab-${id}`)?.focus();
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        className="tables__scroll"
        role="tabpanel"
        id={`panel-${active}`}
        aria-labelledby={`tab-${active}`}
        tabIndex={0}
      >
        {active === "sephiroth" ? (
          <SephirothTable
            sephiroth={sephiroth}
            selected={selected}
            onSelect={onSelect}
            regionOverlay={regionOverlay}
            sequence={sequence && sequence.kind === "sephirah" ? sequence : null}
          />
        ) : (
          <PathsTable
            paths={paths}
            selected={selected}
            onSelect={onSelect}
            sequence={sequence && sequence.kind === "path" ? sequence : null}
          />
        )}
      </div>

      {!hasPaths && pathsNote && (
        <section
          className="paths-note"
          aria-labelledby="paths-note-heading"
          data-testid="paths-note"
        >
          <h2 className="paths-note__heading" id="paths-note-heading">
            On the twenty-two paths
          </h2>
          <p className="paths-note__body">{pathsNote}</p>
        </section>
      )}
    </section>
  );
}
